// src/hooks/useEcommerce.js
import { useCallback, useState } from "react";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { useData } from "./useData";

export const useEcommerce = () => {
  const {
    canvas,
    basketItem,
    signCategoryState,
    setBasketItem,
    setSnackbar,
    setLoading,
    setError,
    signCategories,
    mivaGlobals, // ← Get it from context!
  } = useData();

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const determineFileName = (mivaObj, suffix = "") => {
    const date = new Date();
    return `${mivaObj.basket_id}-${date.getTime()}${suffix}`;
  };

  const addToBasket = useCallback(
    async (quantity = 1) => {
      // ← mivaGlobals comes from context, not param
      if (!mivaGlobals?.basket_id || !mivaGlobals?.session_id) {
        console.error("Missing mivaGlobals for addToBasket", mivaGlobals);
        setSnackbar({
          open: true,
          message: "Session expired. Please refresh.",
          severity: "error",
        });
        return;
      }

      if (!canvas || !basketItem.productCode) {
        setSnackbar({
          open: true,
          message: "Please create a design and select a product.",
          severity: "error",
        });
        return;
      }

      if (isAddingToCart) return;
      setIsAddingToCart(true);
      setLoading(true);

      try {
        const mivaObj = mivaGlobals;
        if (!mivaObj.basket_id) throw new Error("Missing basket_id");

        const category = signCategories.find(
          (c) => c.productCode === basketItem.productCode
        );
        if (!category) throw new Error("Category not found");

        // ----- FILE UPLOADS (SVG + PNG) -----
        const svgData = canvas.toSVG();
        const svgRef = ref(
          storage,
          `basket_templates/${mivaObj.customer_id ?? "guest"}/${
            mivaObj.basket_id
          }/${determineFileName(mivaObj)}.svg`
        );
        await uploadString(
          svgRef,
          `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`,
          "data_url"
        );
        const svgUrl = await getDownloadURL(svgRef);

        const pngRef = ref(
          storage,
          `basket_templates/${mivaObj.customer_id ?? "guest"}/${
            mivaObj.basket_id
          }/${determineFileName(mivaObj, "_thumbnail")}.png`
        );
        await uploadString(
          pngRef,
          canvas.toDataURL({ format: "png", quality: 0.8 }),
          "data_url"
        );
        const pngUrl = await getDownloadURL(pngRef);

        setBasketItem((prev) => ({ ...prev, thumbnailImage: pngUrl }));

        // ----- SELECTED ATTRIBUTES -----
        const selectedSize = category.product_attributes.sizes.options.find(
          (s) =>
            s.width === Number(basketItem.width) &&
            s.height === Number(basketItem.height)
        ) ?? { price: 0, code: "unknown" };

        const selectedGauge = category.product_attributes.gauges?.options.find(
          (g) => g.name === basketItem.gauges
        ) ?? { price: 0, code: "unknown" };

        const selectedAddOns =
          category.product_attributes.addOns?.options.filter((a) =>
            basketItem.addOns?.includes(a.name)
          ) ?? [];

        // ----- VALIDATION -----
        if (!basketItem.gauges && category.product_attributes.gauges)
          throw new Error("Gauge is not selected");
        if (!basketItem.width || !basketItem.height)
          throw new Error("Size is not selected");

        // ----- PRICE CALCULATION -----
        const gaugePrice =
          selectedGauge.price && basketItem.width && basketItem.height
            ? Number(selectedGauge.price) * basketItem.width * basketItem.height
            : 0;

        const totalPrice =
          Number(selectedSize.price ?? 0) +
          gaugePrice +
          selectedAddOns.reduce((s, a) => s + Number(a.price ?? 0), 0);

        // ----- MIVA ATTRIBUTES -----
        const productAttributes = [
          { code: "File_Name", value: pngUrl },
          {
            code: category.product_attributes.sizes.code,
            value: selectedSize.code,
          },
          ...(category.product_attributes.gauges
            ? [
                {
                  code: category.product_attributes.gauges.code,
                  value: selectedGauge.code,
                },
              ]
            : []),
          ...(category.product_attributes.addOns
            ? [
                {
                  code: category.product_attributes.addOns.code,
                  value: selectedAddOns.length
                    ? selectedAddOns.map((a) => a.code).join(", ")
                    : "none",
                },
              ]
            : []),
        ].filter((a) => a.value !== "none" || a.code === "File_Name");

        // ----- MIVA CALL -----
        const jsonBasket = {
          function: "Action_AddProductToBasket",
          Product_Code: basketItem.productCode,
          basket_id: mivaObj.basket_id,
          Quantity: quantity,
          Product_Attribute_Count: productAttributes.length,
          update_price: totalPrice.toFixed(2),
          Product_Attributes: productAttributes,
          session_id: mivaObj.session_id,
        };

        const resp = await fetch(
          `https://www.alphabetsigns.com/mm5/merchant.mvc?Screen=AJXATC&t=${Date.now()}`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(jsonBasket),
          }
        );

        if (!resp.ok) throw new Error(`Basket API failed: ${resp.status}`);

        setSnackbar({
          open: true,
          message: "Product added to cart successfully!",
          severity: "success",
        });

        window.location.href =
          "https://www.alphabetsigns.com/cart-contents.html";
      } catch (err) {
        setError(err.message);
        setSnackbar({
          open: true,
          message: err.message || "Failed to add to cart.",
          severity: "error",
        });
      } finally {
        setIsAddingToCart(false);
        setLoading(false);
      }
    },
    [
      canvas,
      basketItem,
      setBasketItem,
      setSnackbar,
      setLoading,
      setError,
      isAddingToCart,
      signCategories,
      mivaGlobals, // ← Critical: include in deps
    ]
  );

  return { addToBasket };
};
